import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { Crown, ArrowRight, Quote } from "lucide-react";
import { hideOnError } from "../lib/imgFallback";

// Stable per-browser client id used to dedupe spotlight-click counts.
function getClientId() {
  let id = localStorage.getItem("cauldron_client_id");
  if (!id) {
    id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    try { localStorage.setItem("cauldron_client_id", id); } catch { /* private mode */ }
  }
  return id;
}

/**
 * FounderOfTheWeek — Landing-page spotlight card that rotates weekly
 * (deterministic ISO-week seed on the backend). Public, no auth.
 *
 * Compact, hero-adjacent: gold-ringed avatar + name + tradition + a
 * lineage-or-bio pull-quote, with a "Visit profile" CTA that funnels
 * straight to the new public `/elders/{uid}` page.
 */
export default function FounderOfTheWeek() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/providers/elder-of-the-week");
        setData(data);
      } catch { /* noop — silently hide on error */ }
      setLoading(false);
    })();
  }, []);

  if (loading || !data?.elder) return null;
  const e = data.elder;
  const displayName = e.businessName || e.name;
  const initials = (e.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const avatarSrc = e.avatarUrl?.startsWith("http") ? e.avatarUrl : (e.avatarUrl ? `${API}${e.avatarUrl}` : null);
  const pullquote = e.lineageStatement || e.bio;

  const trackClick = () => {
    // Fire-and-forget. Browser will keep the request in-flight across the
    // navigation. Errors are silently ignored — tracking must never block UX.
    try {
      api.post("/providers/elder-of-the-week/track-click", {
        providerId: e.userId, week: data.week, clientId: getClientId(),
      }).catch(() => {});
    } catch { /* noop */ }
  };

  return (
    <Link
      to={`/elders/${e.userId}?from=founder-of-the-week`}
      onClick={trackClick}
      className="block parchment p-6 sm:p-7 hover:translate-y-[-2px] transition-transform group"
      data-testid="founder-of-week-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="label-mystic text-secondary inline-flex items-center gap-2">
          <Crown size={11} strokeWidth={2} /> Founder of the week
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/45" data-testid="founder-of-week-isoweek">
          {data.week}
        </div>
      </div>

      <div className="flex gap-5 items-start">
        {/* Gold-ringed avatar */}
        <div className="relative shrink-0">
          <div
            className="absolute pointer-events-none rounded-full"
            style={{
              inset: "-5px",
              background: "conic-gradient(from 140deg, #C5A059 0deg, #D95C14 90deg, #F4D58E 180deg, #C5A059 270deg, #D95C14 360deg)",
              padding: "3px",
              WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
              boxShadow: "0 0 16px rgba(217,92,20,0.5)",
            }}
          />
          <div className="relative w-20 h-20 rounded-full overflow-hidden bg-secondary/15 border-2 border-card flex items-center justify-center">
            {avatarSrc ? (
              <img src={avatarSrc} alt={displayName} className="w-full h-full object-cover" onError={hideOnError} />
            ) : (
              <span className="font-serif text-2xl text-secondary">{initials}</span>
            )}
          </div>
          <span
            className="absolute flex items-center justify-center rounded-full"
            style={{
              top: "-6px", right: "-6px", width: 26, height: 26,
              background: "linear-gradient(135deg,#D95C14 0%,#C5A059 60%,#F4D58E 100%)",
              color: "#15102A",
              boxShadow: "0 4px 12px rgba(0,0,0,0.4), 0 0 0 2px #15102A",
            }}
          >
            <Crown size={13} strokeWidth={2.25} fill="currentColor" />
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="font-serif text-2xl leading-tight truncate" data-testid="founder-of-week-name">
            {displayName}
          </div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-foreground/55 mt-1">
            {e.elderIndex ? `#${String(e.elderIndex).padStart(4, "0")} · ` : ""}
            {e.tradition || e.category || "Verified Elder"}
            {e.yearsInitiated ? ` · ${e.yearsInitiated}+ yrs` : ""}
          </div>

          {pullquote && (
            <div className="relative pl-5 mt-3">
              <Quote size={12} className="absolute top-1 left-0 text-secondary/60" strokeWidth={1.75} />
              <p className="font-serif italic text-foreground/85 text-[15px] leading-snug line-clamp-3">
                {pullquote}
              </p>
            </div>
          )}

          <div className="text-xs uppercase tracking-[0.22em] text-secondary mt-4 inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all" data-testid="founder-of-week-cta">
            Visit profile <ArrowRight size={11} />
          </div>
        </div>
      </div>
    </Link>
  );
}
